import { useContext, useRef, useState } from "react";
import CloseIcon from "../../assets/img/closeIcon.svg";
import useTranslator from "../../hooks/useTranslator";
import { isValidEmail, toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";

const NewApplicationModal = ({ isOpen, onClose, onSuccess }) => {
  const { _t } = useTranslator();
  const { createApplication, myApplications } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);

  const defaultTitle = `${user?.fname}'s ${_t("application")}`;

  const [appTitle, setAppTitle] = useState(defaultTitle);
  const [editingTitle, setEditingTitle] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [participants, setParticipants] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [creating, setCreating] = useState(false);
  const titleInputRef = useRef(null);

  const handleAddEmail = () => {
    const trimmedName = nameInput.trim();
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedName) {
      setEmailError(_t("nameRequired", "Full name is required"));
      return;
    }
    if (!trimmedEmail) return;
    if (!isValidEmail(trimmedEmail)) {
      setEmailError(_t("invalidEmail"));
      return;
    }
    if (trimmedEmail === user?.email?.toLowerCase()) {
      setEmailError(_t("cannotAddYourself"));
      return;
    }
    if (participants.some((p) => p.email === trimmedEmail)) {
      setEmailError(_t("emailAlreadyAdded"));
      return;
    }
    if (participants.length >= 4) {
      setEmailError(
        _t("maxParticipantsReached", "Maximum of 4 participants allowed")
      );
      return;
    }
    setParticipants((prev) => [
      ...prev,
      { name: trimmedName, email: trimmedEmail },
    ]);
    setNameInput("");
    setEmailInput("");
    setEmailError("");
  };

  const handleRemoveEmail = (email) => {
    setParticipants((prev) => prev.filter((p) => p.email !== email));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleCreate = async () => {
    if (participants.length === 0) {
      toast.error(_t("addAtLeastOneEmail"));
      return;
    }

    const newEmails = participants.map((p) => p.email.toLowerCase()).sort();
    const currentUserEmail = user?.email?.toLowerCase();
    const hasDuplicate = (myApplications ?? []).some((app) => {
      const existing = (app.participants ?? [])
        .map((p) => p.email?.toLowerCase())
        .filter((e) => e && e !== currentUserEmail)
        .sort();
      return (
        existing.length === newEmails.length &&
        existing.every((e, i) => e === newEmails[i])
      );
    });
    if (hasDuplicate) {
      toast.error(_t("alreadyExistingApplication"));
      return;
    }
    try {
      setCreating(true);
      await createApplication({
        participants,
        title: appTitle.trim() || defaultTitle,
      });
      onSuccess();
      handleClose();
      toast.success(_t("applicationCreatedSuccess"));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToCreateApplication")
      );
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setAppTitle(defaultTitle);
    setEditingTitle(false);
    setNameInput("");
    setEmailInput("");
    setParticipants([]);
    setEmailError("");
    onClose();
  };

  const handleEditTitleClick = () => {
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  const handleTitleBlur = () => {
    setEditingTitle(false);
    if (!appTitle.trim()) setAppTitle(defaultTitle);
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal" onClick={handleClose}>
      <div
        className="doc-history-popup total-salary-modal open new-application-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="drawerIcon" onClick={handleClose} />

        {/* Header */}
        <div className="doc-history-header new-application-header">
          <img
            src={CloseIcon}
            alt=""
            className="doc-history-close-btn"
            onClick={handleClose}
          />
          <h4 className="custom-modal-heading">{_t("newApplication")}</h4>
        </div>

        {/* Body */}
        <div className="new-application-body">
          {/* Application name row */}
          <div className="new-application-title-row">
            <span className="new-application-title-label">
              {_t("applicationName", "Application name")}
            </span>
            <div className="new-application-title-value">
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  className="new-application-title-input"
                  value={appTitle}
                  onChange={(e) => setAppTitle(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                  }}
                />
              ) : (
                <>
                  <span className="new-application-title-text">{appTitle}</span>
                  <button
                    className="new-application-title-edit-btn"
                    onClick={handleEditTitleClick}
                    aria-label="edit title"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 20h4l10.5-10.5a2.828 2.828 0 1 0-4-4L4 16v4" />
                      <path d="M13.5 6.5l4 4" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="new-application-field-group">
            <h5 className="new-application-section-title">
              {_t("inviteFlatmatesByEmail")}
            </h5>
            <p className="new-application-section-subtitle sps-trigger-line-reduced">
              {_t(
                "typeEmailAndPressEnter",
                "The invited flatmates will receive a request to confirm they accept being part of this application."
              )}
            </p>

            <div className="new-application-email-row">
              <input
                type="text"
                className={`new-application-email-input${emailError ? " is-invalid" : ""}`}
                placeholder={_t("fullName", "Full Name")}
                value={nameInput}
                onChange={(e) => {
                  setNameInput(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={handleKeyDown}
              />
              <input
                type="email"
                className={`new-application-email-input${emailError ? " is-invalid" : ""}`}
                placeholder="name@email.com"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value.toLowerCase());
                  setEmailError("");
                }}
                onKeyDown={handleKeyDown}
              />
              <button
                className="new-application-add-btn"
                onClick={handleAddEmail}
                disabled={!emailInput.trim() || !nameInput.trim()}
              >
                {_t("add")}
              </button>
            </div>
            {emailError && (
              <p className="new-application-email-error">{emailError}</p>
            )}
          </div>

          <div className="new-application-email-list">
            {participants.map(({ name, email }) => (
              <div key={email} className="new-application-email-item">
                <div className="new-application-participant-info">
                  <span className="new-application-participant-name sps-trigger-line-reduced">
                    {name}
                  </span>
                  <span className="new-application-participant-email sps-trigger-line-reduced">
                    {email}
                  </span>
                </div>
                <span className="new-application-email-status">
                  {_t("invitationWillBeSent", "Invitation will be sent")}
                </span>
                <button
                  className="new-application-email-remove"
                  onClick={() => handleRemoveEmail(email)}
                  aria-label="remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="new-application-footer">
          <div className="new-application-footer-btns">
            <button
              className="new-application-cancel-btn"
              onClick={handleClose}
            >
              {_t("cancel")}
            </button>
            <button
              className="addGuarantorButton d-flex align-items-center justify-content-center"
              onClick={handleCreate}
              disabled={creating}
              style={{ opacity: creating ? 0.6 : 1 }}
            >
              {_t("createApplication")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewApplicationModal;
